import Seo from "@/components/Seo";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES, CONTACT, STATS, OUR_STORY, MISSION } from "@/lib/data";

function PageHero() {
  return (
    <section className="relative pt-40 md:pt-56 pb-16 md:pb-24 max-w-[1400px] mx-auto px-5 md:px-10" data-testid="about-hero">
      <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center gap-3">
        <span className="inline-block w-8 h-px bg-gold" /> About
      </p>
      <h1 className="font-display uppercase font-medium text-[13vw] md:text-[8vw] leading-[0.85] tracking-tighter">
        <MaskedLines lines={["A family name", "you can trust."]} once={false} />
      </h1>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal delay={0.2}>
            <p className="text-lg text-white/70 leading-relaxed">
              Since {CONTACT.since}, Saver Plus Lighting Corp. has supplied and installed lighting across {CONTACT.country}.
              What began as a family business remains one today — and the values haven't changed:
              honest pricing, personal service, and expertise you can actually reach on the phone.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function OurStory() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28" data-testid="our-story">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-20">
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-5">Our Story</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.92] tracking-tight">
            <MaskedLines lines={["Every story", "starts", "somewhere."]} />
          </h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 flex items-end">
          <Reveal delay={0.15}>
            <p className="text-xl md:text-2xl text-white/85 leading-relaxed font-display tracking-tight">
              {OUR_STORY.intro}
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="overflow-hidden aspect-[4/5]">
            <motion.img
              style={{ y, scale: 1.15 }}
              src={IMAGES.toronto}
              alt="Toronto skyline at night"
              className="w-full h-full object-cover"
            />
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.18em] text-white/40">
            Serving the Greater Toronto Area since 1980
          </p>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 space-y-6">
          {OUR_STORY.paragraphs.map((p, i) => {
            const highlight = p.includes("S & S Super Light");
            return (
              <Reveal key={i} delay={i * 0.03}>
                <p className={`leading-relaxed text-justify ${highlight ? "text-lg text-white/85 border-l-2 border-gold pl-5" : "text-white/60"}`}>
                  {p}
                </p>
              </Reveal>
            );
          })}
          <Reveal delay={0.1}>
            <Link
              to="/founder"
              data-testid="about-founder-link"
              className="group inline-flex items-center gap-2 mt-2 text-sm uppercase tracking-[0.14em] text-gold hover:text-white transition-colors duration-300"
            >
              Read his story
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Mission() {
  return (
    <section className="bg-[#0C0C0C] border-y border-white/10 py-24 md:py-32" data-testid="mission">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
        <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-8">Our Mission</p>
        <Reveal>
          <blockquote className="font-display uppercase text-3xl md:text-5xl leading-[1.08] tracking-tight text-balance">
            {MISSION.statement}
          </blockquote>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-white/60 leading-relaxed max-w-2xl mx-auto">{MISSION.closing}</p>
        </Reveal>
      </div>
    </section>
  );
}

function Values() {
  const items = [
    { n: "02", t: "Family-operated", d: "You deal with people who have skin in the game — not a faceless call centre." },
    { n: "03", t: "Personal service", d: "Speak directly with experienced lighting specialists who understand your project." },
    { n: "04", t: "Fast & reliable", d: "Quick quotations, dependable supply, and responsive customer support every time." },
  ];
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="values">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {items.map((v, i) => (
          <Reveal key={v.n} delay={i * 0.08} className="group">
            <div className="h-full bg-[#0A0A0A] p-8 md:p-10 hover:bg-[#121212] transition-colors duration-400">
              <span className="font-display text-white/25 text-lg group-hover:text-gold transition-colors duration-400">{v.n}</span>
              <h3 className="mt-6 font-display uppercase text-2xl tracking-tight">{v.t}</h3>
              <p className="mt-4 text-white/55 leading-relaxed text-sm">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-12" data-testid="about-stats">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-white/10 py-12">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <p className="font-display text-gold text-5xl md:text-6xl leading-none tracking-tight">{s.value}</p>
            <p className="mt-3 text-sm text-white/50 uppercase tracking-[0.12em]">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FirstLogo() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-6, -1]);

  return (
    <section ref={ref} className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28" data-testid="first-logo">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 flex justify-center lg:justify-start">
          <motion.div
            style={{ rotate }}
            className="relative bg-[#F4ECD8] px-10 py-12 md:px-14 md:py-16 shadow-2xl shadow-black/40 max-w-md w-full"
            data-testid="heritage-logo-card"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-white/70 rotate-2" aria-hidden="true" />
            <img
              src="/assets/heritage-logo.png?v=4"
              alt="Original Saver Plus delivery man logo, hand-drawn since 1980"
              className="w-full h-auto"
            />
          </motion.div>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <span className="font-display text-gold text-7xl md:text-8xl leading-none">1980</span>
          <h2 className="mt-6 font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
            <MaskedLines lines={["Our very", "first logo."]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-white/70 leading-relaxed text-justify">
              Our trusty delivery man has been with us since the very beginning in 1980. Hand-drawn in our earliest days,
              he represents the hard work, friendly service, and personal relationships that built Saver Plus Lighting
              Corp. He remains a proud reminder of where we started and of the commitment to our customers that has never
              changed.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div>
      <Seo
        title="About Saver Plus Lighting Corp (SPLC) | Toronto Lighting & Electrical Supplier Since 1980"
        description="The story of Saver Plus Lighting Corp (SPLC) — a family-operated Toronto lighting and electrical supplier serving the GTA since 1980. Founded as S & S Super Light by Shamin & Salema Mohamed, still 100% family-run with no minimum order."
        path="/about"
      />
      <PageHero />
      <StatsBand />
      <OurStory />
      <FirstLogo />
      <Mission />
      <Marquee text="Family Operated" separator="·" />
      <Values />
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 text-center">
        <Reveal>
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
            Let's talk about your project.
          </h2>
          <Link
            to="/contact"
            data-testid="about-quote-cta"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 hover:bg-[#b00000] transition-colors duration-300"
          >
            Request a Quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
